import React, { useState } from 'react';
import { History, CheckCircle, MessageSquare, Download, Trash2 } from 'lucide-react';
import { useApp } from '../context/AppContext';

const HistoryPanel: React.FC = () => {
  const { state, dispatch } = useApp();
  const [selectedCalculation, setSelectedCalculation] = useState<string | null>(null);
  const [annotationText, setAnnotationText] = useState('');

  const loadCalculation = (calculation: any) => {
    dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: calculation.expression });
    dispatch({ type: 'SET_CURRENT_RESULT', payload: calculation.result });
    dispatch({ type: 'SET_ERROR', payload: { isError: false, message: '' } });
  };

  const approveCalculation = (calculationId: string) => {
    dispatch({ type: 'APPROVE_CALCULATION', payload: { calculationId, userId: state.currentUser.id } });
  };

  const addAnnotation = (calculationId: string) => {
    if (annotationText.trim()) {
      dispatch({ type: 'ADD_ANNOTATION', payload: { calculationId, annotation: annotationText } });
      setAnnotationText('');
      setSelectedCalculation(null);
    }
  };

  const exportHistory = () => {
    const historyText = state.calculations
      .map(calc => {
        try {
          const date = calc.timestamp instanceof Date 
            ? calc.timestamp 
            : (calc.timestamp as any).toDate();
          return `${date.toLocaleString()} - ${calc.userName}: ${calc.expression} = ${calc.result}`;
        } catch (error) {
          return `Invalid time - ${calc.userName}: ${calc.expression} = ${calc.result}`;
        }
      })
      .join('\n');
    
    const blob = new Blob([historyText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'decalculator-history.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    if (window.confirm('Are you sure you want to clear all calculations?')) {
      dispatch({ type: 'CLEAR_ALL' });
    }
  };

  return (
    <div className="bg-dark-800 rounded-xl p-4 h-full flex flex-col shadow-lg border border-dark-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <History className="h-5 w-5 text-accent-500" />
          <h3 className="text-lg font-semibold text-white">History</h3>
          <span className="text-sm text-dark-400">({state.calculations.length})</span>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={exportHistory}
            className="p-2 text-dark-400 hover:text-white transition-colors rounded-lg hover:bg-dark-700"
            title="Export History"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={clearAll}
            className="p-2 text-dark-400 hover:text-danger-400 transition-colors rounded-lg hover:bg-dark-700"
            title="Clear All"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* History List */}
      <div className="flex-1 overflow-y-auto space-y-2 scrollbar-hide">
        {state.calculations.length === 0 ? (
          <div className="text-center text-dark-500 py-8">
            <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No calculations yet</p>
            <p className="text-sm">Start calculating to see history</p>
          </div>
        ) : (
          (state.calculations || []).map((calculation) => (
            <div
              key={calculation.id}
              className="bg-dark-700 rounded-lg p-4 space-y-3 hover:bg-dark-600 transition-all duration-200 border border-dark-600 animate-slide-up"
            >
              {/* User Info and Timestamp */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold border-2 border-dark-600"
                    style={{ backgroundColor: calculation.userColor }}
                  >
                    {calculation.userName.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm text-dark-200 font-medium">{calculation.userName}</span>
                </div>
                <span className="text-xs text-dark-500">
                  {(() => {
                    try {
                      const date = calculation.timestamp instanceof Date 
                        ? calculation.timestamp 
                        : (calculation.timestamp as any).toDate();
                      return date.toLocaleTimeString();
                    } catch (error) {
                      return 'Invalid time';
                    }
                  })()}
                </span>
              </div>

              {/* Calculation */}
              <div className="space-y-2">
                <div className="text-sm font-mono text-dark-300 bg-dark-800 p-2 rounded border">
                  {calculation.expression}
                </div>
                <div className="text-xl font-mono font-bold text-success-400">
                  = {calculation.result}
                </div>
              </div>

              {/* Annotation */}
              {calculation.annotation && (
                <div className="bg-dark-600 rounded-lg p-3 text-sm text-dark-200 border border-dark-500">
                  <div className="flex items-center space-x-2 mb-2">
                    <MessageSquare className="h-4 w-4 text-accent-500" />
                    <span className="font-medium text-accent-400">Note:</span>
                  </div>
                  {calculation.annotation}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={() => loadCalculation(calculation)}
                    className="text-xs bg-accent-600 hover:bg-accent-700 text-white px-3 py-1.5 rounded-lg transition-all duration-200 font-medium"
                  >
                    Load
                  </button>
                  
                  {state.isMultiplayerMode && (
                    <button
                      onClick={() => setSelectedCalculation(calculation.id)}
                      className="text-xs bg-dark-600 hover:bg-dark-700 text-white px-3 py-1.5 rounded-lg transition-all duration-200"
                    >
                      <MessageSquare className="h-3 w-3" />
                    </button>
                  )}
                </div>

                {/* Approval Status */}
                {state.isMultiplayerMode && (
                  <div className="flex items-center space-x-2">
                    {calculation.approvedBy.length > 0 && (
                      <div className="flex -space-x-1">
                        {calculation.approvedBy.map((userId) => {
                          const user = state.sessionUsers.find(u => u.id === userId);
                          return user ? (
                            <div
                              key={userId}
                              className="w-5 h-5 rounded-full border-2 border-dark-800 flex items-center justify-center text-xs font-semibold"
                              style={{ backgroundColor: user.color }}
                              title={`Approved by ${user.name}`}
                            >
                              ✓
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}
                    
                    <button
                      onClick={() => approveCalculation(calculation.id)}
                      className={`p-1.5 rounded-lg transition-all duration-200 ${
                        calculation.approvedBy.includes(state.currentUser.id)
                          ? 'text-success-400 bg-success-900'
                          : 'text-dark-400 hover:text-success-400 hover:bg-success-900'
                      }`}
                      title="Approve calculation"
                    >
                      <CheckCircle className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Annotation Modal */}
      {selectedCalculation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-xl p-6 w-96 shadow-2xl border border-dark-700">
            <h3 className="text-lg font-semibold text-white mb-4">Add Annotation</h3>
            <textarea
              value={annotationText}
              onChange={(e) => setAnnotationText(e.target.value)}
              placeholder="Add a note about this calculation..."
              className="w-full h-24 bg-dark-700 text-white rounded-lg p-3 resize-none focus:outline-none focus:ring-2 focus:ring-accent-500 border border-dark-600"
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => {
                  setSelectedCalculation(null);
                  setAnnotationText('');
                }}
                className="px-4 py-2 bg-dark-600 text-white rounded-lg hover:bg-dark-700 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => addAnnotation(selectedCalculation)}
                className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-all duration-200 font-medium shadow-md"
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryPanel;
